import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createModifier, restoreModifier, updateModifier } from 'api/Admin/Modifiers';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Modal from 'components/shared/Modal/Modal';

import useModifiersFormik from '../configs/useModifiersFormik';

import ModifierForm from './ModifierForm';

const ModifierModal = ({ isOpen, handleClose, modifierData = {}, modalType = 'new' }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [formikData, setFormikData] = useState(modifierData);
  const [restoreModal, setRestoreModal] = useState(false);

  const activateRestoreModal = () => {
    setRestoreModal(true);
  };

  const createNewModifier = useMutation({
    mutationFn: (data) => {
      return createModifier(navigate, data);
    },
    onSuccess: async (data) => {
      if (data.code === 8) {
        return activateRestoreModal();
      }
      if (data.code === 0) {
        await queryClient.invalidateQueries(['modifiers']);
        showAlert({ title: 'Modifier created successfully', color: 'success' });
        formik.resetForm();
        handleClose();
      } else {
        showAlert({
          title: data?.error ?? 'Something went wrong creating modifier',
          color: 'danger'
        });
      }
    }
  });

  const editModifier = useMutation({
    mutationFn: (data) => {
      return updateModifier(navigate, {
        code_used: modifierData?.code,
        code_update: data?.code,
        name: data?.name
      });
    },
    onSuccess: async (data) => {
      if (data.code === 0) {
        await queryClient.invalidateQueries(['modifiers']);
        showAlert({ title: 'Modifier edited successfully', color: 'success' });
        formik.resetForm();
        handleClose();
      } else {
        showAlert({
          title: data?.error ?? 'Something went wrong updating modifier',
          color: 'danger'
        });
      }
    }
  });

  const restoreModifierInstance = useMutation({
    mutationFn: (data) => {
      return restoreModifier(navigate, data);
    },
    onSuccess: async (data) => {
      if (data.code === 0) {
        await queryClient.invalidateQueries(['modifiers']);
        showAlert({ title: 'Modifier restored successfully', color: 'success' });
        formik.resetForm();
        handleClose();
      } else {
        showAlert({
          title: data?.error ?? 'Something went wrong creating modifier',
          color: 'danger'
        });
      }
    }
  });

  const onSubmit = async (values) => {
    if (modalType == 'new') {
      await createNewModifier.mutateAsync(values);
    } else {
      await editModifier.mutateAsync(values);
    }
  };

  const formik = useModifiersFormik(modifierData ? formikData : {}, onSubmit);

  useEffect(() => {
    setFormikData(modifierData);
  }, [isOpen]);

  const closeModal = () => {
    setFormikData(null);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={closeModal}
      slideFromRight
      title={modalType == 'new' ? 'New Modifier' : 'Edit Modifier'}
      footer={
        <div className="flex w-full justify-between gap-4">
          <Button
            outlined
            color="neutral"
            text="Close"
            onClick={() => {
              handleClose();
            }}
          />
          <Button
            disabled={createNewModifier.isLoading || editModifier.isLoading}
            loading={createNewModifier.isLoading || editModifier.isLoading}
            outlined
            color="primary"
            text="Submit"
            onClick={formik.handleSubmit}
          />
        </div>
      }>
      <ModifierForm formik={formik} />
      <Confirm
        isOpen={restoreModal}
        title="Restore Modifier"
        message="The code you're trying to use belongs to a deleted modifier. Restore it?"
        variant="warning"
        icon="info-filled"
        primaryBtnTxt="Restore"
        loading={restoreModifierInstance.isLoading}
        disabled={restoreModifierInstance.isLoading}
        handleClose={() => setRestoreModal(false)}
        handleContinue={() => {
          restoreModifierInstance.mutate({ code: formik?.values?.code });
        }}
      />
    </Modal>
  );
};

export default withErrorBoundary(ModifierModal);
